// RB-MY01-PalmGarden.web/src/components/ProtectedRoute.tsx
import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Box, CircularProgress } from '@mui/material';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requireAdmin = false 
}) => {
  const { isAuthenticated, isLoading, user, error } = useAuth();
  const isAdmin = user?.isAdmin === true;
  const location = useLocation();
  
  // Enhanced logging
  useEffect(() => {
    console.log('ProtectedRoute state:', { 
      path: location.pathname,
      isAuthenticated, 
      isAdmin, 
      isLoading,
      user,
      error,
      requireAdmin 
    });
  }, [isAuthenticated, isAdmin, isLoading, user, error, requireAdmin, location]);
  
  // Show a better loading indicator
  if (isLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '70vh' 
      }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // If not authenticated, redirect to login with the current location
  if (!isAuthenticated) {
    console.log('User not authenticated, redirecting to login from:', location.pathname);
    
    // Save the current location to redirect back after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  // If admin access is required but user is not admin
  if (requireAdmin && !isAdmin) {
    console.log('Admin access required but user is not admin');
    return <Navigate to="/unauthorized" replace />;
  }
  
  // User is authenticated and has required permissions
  console.log('User authenticated, rendering protected content');
  return <>{children}</>;
};

export default ProtectedRoute; 